/**
 * Demo data for the project protocol (?mock=1 and tests), on the demo projects and machines of
 * lib/mock.ts. Qawl-Agents reports through the protocol on studio (a landing of 4 submissions in
 * progress, so heavy work there waits), and through the temporary adapter on mbp-16;
 * billing-service's health file stopped reporting; orgus declares actions and writes no state
 * files; docs-site uses none of it.
 */
import { DEFAULT_NATIVE_CHECK_SETTINGS, PROJECT_YIELD_MAX_MS } from '@orgus/protocol';
import type { ProjectActionDecl, ProjectBusy, ProjectManifest, ProjectProtocolState, StateFileStatus, YieldHold } from '@orgus/protocol';
import { MOCK_IDS } from '../../lib/mock.ts';

const S = 1000;
const MIN = 60 * S;
const H = 60 * MIN;
const M = MOCK_IDS;

/** Ids of the held work: the AI jobs demo (requirements/mock.ts) carries the queued job; the run is in this file; the mission queue entry exists only on a hub that holds one. */
export const PP_MOCK = {
  heldQueueEntry: 'que_demo_report',
  heldMission: 'msn_demo_report',
  heldAiJob: 'job_demo_summary',
  heldRun: 'run_demo_index',
} as const;

const act = (id: string, label: string, run: string, does: string, extra: Partial<ProjectActionDecl> = {}): ProjectActionDecl => ({
  id,
  label,
  run,
  does,
  cwd: '.',
  confirm: true,
  heavy: false,
  timeoutS: 600,
  ...extra,
});

const QAWL_MANIFEST: ProjectManifest = {
  protocol: 1,
  name: 'Qawl',
  description: 'Agents that land and deploy Qawl',
  pipeline: { title: 'Landing queue', intervalS: 10 },
  health: { intervalS: 60 },
  deploys: { intervalS: 300 },
  protect: ['C:/tmp/qawl-lander'],
  actions: [
    act('pause-lander', 'Pause the lander', 'node scripts/verify/land.mjs --pause', 'The lander stops between batches; the batch at work finishes.', { role: 'pipeline.pause' }),
    act('resume-lander', 'Resume the lander', 'node scripts/verify/land.mjs --resume', 'The lander takes the next batch from the queue.', { role: 'pipeline.resume' }),
    act('restart-lander', 'Restart the lander', 'node scripts/verify/land.mjs --restart', 'The lander process stops and starts again; the batch at work runs again.', { role: 'pipeline.restart' }),
    act('redeploy-console', 'Redeploy the console', 'pnpm --filter console deploy', 'Builds the console from main and uploads it to Pages.', { cwd: '.', timeoutS: 1200 }),
    act('rerun-flaky', 'Rerun flaky specs', 'node scripts/verify/flaky.mjs --rerun', 'Runs the quarantined specs 5 times each and reports which still fail.', { heavy: true, timeoutS: 1800 }),
  ],
};

const ORGUS_MANIFEST: ProjectManifest = {
  protocol: 1,
  name: 'Orgus',
  protect: [],
  actions: [
    act('rebuild-search-index', 'Rebuild the search index', 'pnpm --filter @orgus/web build:search', 'Indexes every page and doc for the command palette.', { cwd: 'apps/web', heavy: true }),
    act('check-layout', 'Check the folder layout', 'node tools/orgus-cli/bin/orgus.mjs layout --tracked', 'Lists tracked files outside the allowed tree. Read-only.'),
  ],
};

const BILLING_MANIFEST: ProjectManifest = {
  protocol: 1,
  name: 'billing-service',
  health: { intervalS: 60 },
  deploys: { intervalS: 300 },
  protect: [],
  actions: [act('replay-webhooks', 'Replay failed webhooks', 'npm run webhooks:replay -- --failed', 'Sends every failed Stripe webhook of the last 24 hours again.')],
};

const file = (kind: StateFileStatus['kind'], state: StateFileStatus['state'], updatedAt: number | null, intervalS: number, reason?: string): StateFileStatus => ({
  kind,
  state,
  updatedAt,
  intervalS,
  ...(reason ? { reason } : {}),
});

export function createMockProjectProtocol(now: number = Date.now()): ProjectProtocolState {
  const landing: ProjectBusy = { kind: 'landing', projectId: M.qawl, machineId: M.studio, what: 'landing 4 submissions', since: now - 9 * MIN };
  const hold = (id: string, kind: YieldHold['kind'], label: string, workId: string, projectId: string | null, sinceAgo: number): YieldHold => ({
    id,
    kind,
    label,
    workId,
    projectId,
    machineId: M.studio,
    for: landing,
    since: now - sinceAgo,
    until: now - sinceAgo + PROJECT_YIELD_MAX_MS,
    state: 'waiting',
  });
  const earlier: ProjectBusy = { kind: 'landing', projectId: M.qawl, machineId: M.studio, what: 'landing 3 submissions', since: now - 70 * MIN };

  return {
    projects: [
      {
        projectId: M.qawl,
        machineId: M.studio,
        manifest: QAWL_MANIFEST,
        files: [file('pipeline', 'reporting', now - 2 * S, 10), file('health', 'reporting', now - 21 * S, 60), file('deploys', 'reporting', now - 94 * S, 300)],
        problems: [],
        deploys: [
          {
            id: 'console',
            label: 'Console',
            url: 'https://console.qawl.example',
            deploying: false,
            live: { version: 'a1b2c3d', at: now - 41 * MIN, source: 'pipeline' },
            mainVersion: 'f4e5d6c',
            behind: true,
            last: { result: 'failed', version: 'f4e5d6c', at: now - 2 * H - 18 * MIN, logRef: 'deploy:console' },
          },
          {
            id: 'runtime',
            label: 'Runtime',
            url: null,
            deploying: false,
            live: { version: 'f4e5d6c', at: now - 41 * MIN, source: 'pipeline' },
            mainVersion: 'f4e5d6c',
            behind: false,
            last: { result: 'succeeded', version: 'f4e5d6c', at: now - 41 * MIN, logRef: null },
          },
        ],
        busy: [landing],
        pipelineSource: 'protocol',
        readAt: now - 2 * S,
      },
      {
        projectId: M.qawl,
        // The laptop's lander stopped writing pipeline.json: the board says since when.
        machineId: M.mbp,
        manifest: QAWL_MANIFEST,
        files: [file('pipeline', 'not-reporting', now - 14 * MIN, 10, 'Older than 3 x 10 s'), file('health', 'reporting', now - 33 * S, 60), file('deploys', 'reporting', now - 2 * MIN, 300)],
        problems: [],
        deploys: [],
        busy: [],
        pipelineSource: 'protocol',
        readAt: now - 5 * S,
      },
      {
        projectId: M.orgus,
        machineId: M.studio,
        manifest: ORGUS_MANIFEST,
        files: [file('pipeline', 'absent', null, 15), file('health', 'absent', null, 60), file('deploys', 'absent', null, 300)],
        problems: [],
        deploys: [],
        busy: [],
        pipelineSource: 'none',
        readAt: now - 3 * S,
      },
      {
        projectId: M.billing,
        machineId: M.mbp,
        manifest: BILLING_MANIFEST,
        files: [
          file('pipeline', 'absent', null, 15),
          file('health', 'not-reporting', now - 14 * MIN, 60, 'Older than 3 x 60 s'),
          file('deploys', 'reporting', now - 3 * MIN, 300),
        ],
        problems: [],
        deploys: [
          {
            id: 'production',
            label: 'Production',
            url: 'https://billing.acme.example',
            deploying: false,
            live: { version: '4be0c3a', at: now - 26 * H, source: 'manual' },
            mainVersion: '4be0c3a',
            behind: false,
            last: { result: 'succeeded', version: '4be0c3a', at: now - 26 * H, logRef: null },
          },
        ],
        busy: [],
        pipelineSource: 'none',
        readAt: now - 6 * S,
      },
      {
        projectId: M.docs,
        machineId: M.mbp,
        manifest: null,
        files: [file('pipeline', 'absent', null, 15), file('health', 'absent', null, 60), file('deploys', 'absent', null, 300)],
        problems: [],
        deploys: [],
        busy: [],
        pipelineSource: 'none',
        readAt: now - 7 * S,
      },
    ],
    problems: [
      {
        key: `orgus:${M.studio}:cpu-saturated`,
        id: 'cpu-saturated',
        source: 'orgus',
        check: 'cpu-saturated',
        severity: 'critical',
        what: 'studio CPU at 98% for 12m',
        why: 'tsc 34% (9 processes, orgus), playwright 28% (Qawl-Agents), claude 14%',
        next: 'Let the landing finish; pause optional work if it lasts. Orgus heavy work already waits.',
        projectId: null,
        machineId: M.studio,
        since: now - 12 * MIN,
        subject: { kind: 'machine', id: M.studio, label: 'studio' },
      },
      {
        key: `project:${M.qawl}:console-deploy-failing`,
        id: 'console-deploy-failing',
        source: 'project',
        severity: 'critical',
        what: 'The console deploy fails on every landing',
        why: 'Pages rejected the last 2 uploads: the build output folder is empty',
        next: 'Check the console build script, then redeploy the console',
        projectId: M.qawl,
        machineId: M.studio,
        since: now - 2 * H - 18 * MIN,
        actionId: 'redeploy-console',
        subject: { kind: 'deploy-target', id: 'console', label: 'Console' },
      },
      {
        key: `project:${M.qawl}:verify-pool-held`,
        id: 'verify-pool-held',
        source: 'project',
        severity: 'warning',
        what: 'One verify run holds 28 of 30 test workers',
        why: '18 tasks from other checkouts wait for a worker',
        next: 'Let the batch finish, or pause the lander if other checkouts need workers now',
        projectId: M.qawl,
        machineId: M.studio,
        since: now - 9 * MIN,
        actionId: 'pause-lander',
      },
      {
        key: `orgus:${M.studio}:worktree-orphaned:c-qawl-voice`,
        id: 'worktree-orphaned:c-qawl-voice',
        source: 'orgus',
        check: 'worktree-orphaned',
        severity: 'warning',
        what: 'fix/voice-latency is held by an ended session',
        why: 'No live session has worked in it for 2h; it keeps 6 uncommitted changes',
        next: 'Commit or discard the changes, then remove the worktree',
        projectId: M.qawl,
        machineId: M.studio,
        since: now - 2 * H,
        subject: { kind: 'worktree', id: 'C:/tmp/qawl-wt-voice-latency', label: 'fix/voice-latency' },
      },
      {
        key: `orgus:${M.billing}:project-not-reporting:health`,
        id: 'project-not-reporting:health',
        source: 'orgus',
        check: 'project-not-reporting',
        severity: 'warning',
        what: 'billing-service health is not reporting',
        why: 'health.json was last written 14m ago; it is due every 60s',
        next: 'Check that the health writer runs on mbp-16',
        projectId: M.billing,
        machineId: M.mbp,
        since: now - 11 * MIN,
      },
      {
        key: `orgus:${M.qawl}:${M.mbp}:project-not-reporting:pipeline`,
        id: 'project-not-reporting:pipeline',
        source: 'orgus',
        check: 'project-not-reporting',
        severity: 'warning',
        what: 'Qawl-Agents landing queue on mbp-16 is not reporting',
        why: 'pipeline.json was last written 14m ago; it is due every 10s',
        next: 'Check that the lander runs on mbp-16, or restart it',
        projectId: M.qawl,
        machineId: M.mbp,
        since: now - 13 * MIN,
        actionId: 'restart-lander',
      },
      {
        key: `project:${M.qawl}:flaky-quarantine`,
        id: 'flaky-quarantine',
        source: 'project',
        severity: 'info',
        what: '2 specs are quarantined as flaky',
        why: 'Each failed and then passed on retry 3 times this week',
        next: 'Fix or delete them before the quarantine ends on Friday',
        projectId: M.qawl,
        machineId: M.studio,
        since: now - 26 * H,
        actionId: 'rerun-flaky',
      },
    ],
    yields: [
      hold('yld_demo_report', 'mission', 'Weekly cost report', PP_MOCK.heldQueueEntry, M.orgus, 6 * MIN),
      hold('yld_demo_summary', 'ai-job', 'Split: Self-serve machine onboarding', PP_MOCK.heldAiJob, M.orgus, 5 * MIN),
      hold('yld_demo_design', 'orgus-check', 'Design gate on orgus', 'chk_demo_design', M.orgus, 4 * MIN),
      hold('yld_demo_index', 'declared-action', 'Rebuild the search index', PP_MOCK.heldRun, M.orgus, 3 * MIN),
      {
        id: 'yld_demo_old',
        kind: 'ai-job',
        label: 'Daily summary',
        workId: 'job_demo_daily',
        projectId: null,
        machineId: M.studio,
        for: earlier,
        since: now - 68 * MIN,
        until: now - 68 * MIN + PROJECT_YIELD_MAX_MS,
        state: 'released',
        releasedAt: now - 57 * MIN,
      },
    ],
    runs: [
      {
        id: PP_MOCK.heldRun,
        projectId: M.orgus,
        machineId: M.studio,
        actionId: 'rebuild-search-index',
        command: 'pnpm --filter @orgus/web build:search',
        cwd: 'apps/web',
        // Held for the landing: it has not started.
        state: 'running',
        requestedBy: 'owner',
        confirmedBy: 'owner',
        yieldId: 'yld_demo_index',
      },
      {
        // Requested from the board on another tab: it waits for a person to confirm it.
        id: 'run_demo_restart',
        projectId: M.qawl,
        machineId: M.mbp,
        actionId: 'restart-lander',
        command: 'node scripts/verify/land.mjs --restart',
        cwd: '.',
        state: 'awaiting-confirmation',
        requestedBy: 'owner',
      },
      {
        id: 'run_demo_resume',
        projectId: M.qawl,
        machineId: M.studio,
        actionId: 'resume-lander',
        command: 'node scripts/verify/land.mjs --resume',
        cwd: '.',
        state: 'succeeded',
        requestedBy: 'owner',
        confirmedBy: 'owner',
        startedAt: now - 1 * H - 50 * MIN,
        endedAt: now - 1 * H - 50 * MIN + 2 * S,
        exitCode: 0,
        logRef: 'run:run_demo_resume',
      },
      {
        id: 'run_demo_replay',
        projectId: M.billing,
        machineId: M.mbp,
        actionId: 'replay-webhooks',
        command: 'npm run webhooks:replay -- --failed',
        cwd: '.',
        state: 'failed',
        requestedBy: 'owner',
        confirmedBy: 'owner',
        startedAt: now - 40 * MIN,
        endedAt: now - 39 * MIN,
        exitCode: 1,
        reason: 'Exited with code 1: STRIPE_KEY is not set in this shell.',
        logRef: 'run:run_demo_replay',
      },
    ],
    dispatches: [
      // ADR 0064: Orgus fixing a problem on its own, on its second try.
      { id: 'fix_demo_auto', problemKey: `project:${M.qawl}:console-deploy-failing`, projectId: M.qawl, machineId: M.studio, sessionId: null, state: 'started', by: 'system:autofix', at: now - 6 * MIN, auto: true, attempt: 2 },
    ],
    settings: { ...DEFAULT_NATIVE_CHECK_SETTINGS, autoFix: true, autoFixBy: 'owner' },
  };
}
